import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { authClient } from '../lib/auth-client';
import { apiFetch } from '../lib/api';
import { captureDemoRef, readDemoRef, withDemoRef } from '../lib/demo-ref';
import { useT } from '../locales/context';

export function SignUp() {
  const t = useT();
  const navigate = useNavigate();
  // Playground-signup-card cycle (THI-33, 2026-08-22 spec §B) — capture `?demo=` into
  // `localStorage` on mount so it survives the sign-up flow's cross-tab email-verification
  // hop; `useDemoRefSubmit` (fired from `Protected` once a session exists) posts it.
  useEffect(() => {
    captureDemoRef(window.location.search);
  }, []);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: providers } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () =>
      apiFetch<{ emailPassword: boolean; google: boolean }>('/api/meta/auth-providers'),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const { error: signUpError } = await authClient.signUp.email({
      name,
      email,
      password,
      callbackURL: '/verify-email',
    });
    setIsSubmitting(false);
    if (signUpError) {
      setError(signUpError.message ?? t.auth.genericFormError);
      return;
    }
    navigate('/check-email');
  }

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t.auth.signUpTitle}</CardTitle>
            <CardDescription>{t.auth.signUpSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="name">{t.auth.nameLabel}</Label>
                <Input
                  id="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">{t.auth.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">{t.auth.passwordLabel}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.auth.creatingAccount : t.auth.createAccountCta}
              </Button>
              {providers?.google && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    authClient.signIn.social({
                      provider: 'google',
                      callbackURL: withDemoRef('/', readDemoRef()),
                    })
                  }
                >
                  {t.auth.continueWithGoogle}
                </Button>
              )}
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t.auth.haveAccountPrompt}
              <Link to="/sign-in" className="underline">
                {t.auth.signInLink}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
